<style scoped>
	@import url("../../../../css/choice.css");
	#body {
		font-family: Tahoma, Arial, MS Sans Serif, Helvetica;
	}

	.header {
		border: 1pt #6a8ccb solid;
		background-color: #dbebff;
		border-collapse: collapse;
	}

	.chart-options {
		margin-top: 2px;
		font-size: 9pt;
	}

	.chart-option {
		margin-left: 5px;
		padding-left: 5px;
	}
</style>

<template>
<div class="row header d-flex align-self-center">
	<div class="col-5">
				<Toolbar
					v-bind:configuration="MainToolbar"
					:style-as-office-md="true"
				>
				</Toolbar>
				</div>
				<div class="col-7 align-self-center">
			<PatientHeader 
                                :displayContext="PortalType.Emr"
                                :legacyPage="false"
								:patientEntityId="PatientEntityId" />   
		</div>
        </div>

	<div class="m-1">
		<div class="row d-flex">
			<div class="col-12">
				<div id="GraphLine" class="graph border border-2 border-dark rounded-2">
					<div id="LabGraphDiv"></div>
				</div>
			</div>
		</div>
		<div class="mt-1 d-flex">
			<div class="col-4 align-middle">
				<div id="ChartOptions" class="chart-options">
					<div class="chart-option">
						<Checkbox
							@update:model-value="UpdateGraph('reverse')"
							v-model="ShowReverse"
						/>Reverse Flow
					</div>
					<div class="chart-option">
						<Checkbox
							@update:model-value="UpdateGraph('ygrid')"
							v-model="ShowYGrid"
						/>Show Horizontal Grid
					</div>
					<div class="chart-option">
						<Checkbox
							@update:model-value="UpdateGraph('xgrid')"
							v-model="ShowXGrid"
						/>Show Vertical Grid
					</div>
				</div>
			</div>

			<div class="col-8 d-flex justify-content-end">
				<div class="d-inline">
				<ChoiceButtonGroup
				:items="GraphRangeButtons"
				:selectedButtonStyle="ButtonDisplayStyle.TextSuccess"
				:buttonSize="ButtonDisplaySize.Small"
				>
				</ChoiceButtonGroup>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { computed, nextTick, onMounted, reactive, ref, StyleValue } from "vue";
	import Checkbox from "../../../components/Inputs/Checkbox.vue";
	import { ActionButtonStyle } from "../../../lib/Types/Types";
	import ChoiceButtonGroup from "../../../components/ChoiceButtonGroup.vue";
	import * as Highcharts from "highcharts";
	import Exporting from "highcharts/modules/exporting";
	import { ButtonDisplayStyle } from "../../../Enums/UiEnums";
	import { ButtonDisplaySize } from "../../../Enums/UiEnums";
	import OfflineExporting from "highcharts/modules/offline-exporting";
	import PatientHeader from "../../../components/Choice/Patient/PatientHeader.vue";
	import {
		IToolbar,
		IToolbarDropDownButton,
		IToolbarDivItem,
		ToolbarItemType,
	} from "../../../lib/Toolbar";
	import Toolbar from "../../../components/Toolbar.vue";
	import { PortalType } from "../../../Enums/ChoiceEnums";
import { ILabFlowGraphInput } from "./ILabFlowGraphInput";
import { IChoiceButtonGroupItem } from "../../../lib/Models/ChoiceButtonGroup";

	const props = withDefaults(defineProps<{
		graphdata: ILabFlowGraphInput
    }>(), {});

	enum MainToolbarItems {
		btnDisableAll,
		btnEnableAll,
		drpChartType
	}

	enum ChartTypeItems {
		Line,
		Spline,
		Area,
		AreaSpline,
		Column,
		Bar,
		Scatter,
	}

	const GraphData: ILabFlowGraphInput = props.graphdata;
	const PatientEntityId = ref<number>(parseInt(GraphData.PatientEntityId ?? ""));
	const GraphRangeButtons: IChoiceButtonGroupItem[] = [
		{ label: "3 mo", value: "3", click: () => { SetTimeLimit(3); } },
		{ label: "6 mo", value: "6", click: () => { SetTimeLimit(6); } },
		{ label: "9 mo", value: "9", click: () => { SetTimeLimit(9); } },
		{ label: "1 yr", value: "12", click: () => { SetTimeLimit(12); } },
		{ label: "2 yr", value: "24", click: () => { SetTimeLimit(24); } },
		{ label: "max", value: "3", click: () => { SetTimeLimit(-1); } },
	];

	// chart
	const MainChart = ref<Highcharts.Chart | null>(null);
	const ChartType = ref<string>("line");
	let ChartMonthTimeLimit: number = -1;
	const ShowReverse = ref<boolean>(true);
	const ShowYGrid = ref<boolean>(true);
	const ShowXGrid = ref<boolean>(true);

  // toolbar
  const MainToolbar = computed<IToolbar>(() => ({
		toolbarList: [
      ToolbarDivDisableAll.value,
			ToolbarDivEnableAll.value,
			ToolbarChartType.value
		],
	}));

  const ToolbarDivEnableAll = ref<IToolbarDivItem>({
		identifier: MainToolbarItems.btnEnableAll,
		divContent: "Enable All",
		tooltip: "Enable all graph series",
		enabled: true,
		toolBarItemType: ToolbarItemType.div,
		performAction: async () => {
			EnableAllSeries();
		},
	});

  const ToolbarDivDisableAll = ref<IToolbarDivItem>({
		identifier: MainToolbarItems.btnDisableAll,
		divContent: "Disable All",
		tooltip: "Disable all graph series",
		enabled: true,
		toolBarItemType: ToolbarItemType.div,
		performAction: async () => {
			DisableAllSeries();
		},
	});

  const ToolbarChartType = ref<IToolbarDropDownButton>({
		identifier: MainToolbarItems.drpChartType,
		label: "Chart Type",
    onMouseEnterClass: "isalus-menu-highlight",
    baseClass: "isalus-menu-item",
		action: ActionButtonStyle.Edit,
		menuList: [
			{
				identifier: ChartTypeItems.Line,
				label: "Line",
				performAction: async () => {
					SetChartType("line");
				},
			},
			{
				identifier: ChartTypeItems.Spline,
				label: "Spline",
				performAction: async () => {
					SetChartType("spline");
				},
			},
			{
				identifier: ChartTypeItems.Area,
				label: "Area",
				performAction: async () => {
					SetChartType("area");
				},
			},
			{
				identifier: ChartTypeItems.AreaSpline,
				label: "AreaSpline",
				performAction: async () => {
					SetChartType("areaspline");
				},
			},
			{
				identifier: ChartTypeItems.Column,
				label: "Column",
				performAction: async () => {
					SetChartType("column");
				},
			},
			{
				identifier: ChartTypeItems.Bar,
				label: "Bar",
				performAction: async () => {
					SetChartType("bar");
				},
			},
			{
				identifier: ChartTypeItems.Scatter,
				label: "Scatter",
				performAction: async () => {
					SetChartType("scatter");
				},
			},
		],
	});

	onMounted(async () => {
		if (typeof Exporting === "function") {
			Exporting(Highcharts);
		}
		if (typeof OfflineExporting === "function") {
			OfflineExporting(Highcharts);
		}

		await LoadGraphData();
	});

	function ChartTimeString(): string {
		if(ChartMonthTimeLimit>0)
			return `${ChartMonthTimeLimit} months`;
		return "All Values";
	}

	function GetChartColor(series: number): string {
		while (series >= 10) {
			series -= 10;
		}
		switch (series) {
			case 0:
				return "#2caffe";
			case 1:
				return "#544fc5";
			case 2:
				return "#00e272";
			case 3:
				return "#fe6a35";
			case 4:
				return "#6b8abc";
			case 5:
				return "#d568fb";
			case 6:
				return "#2ee0ca";
			case 7:
				return "#fa4b42";
			case 8:
				return "#feb56a";
			case 9:
				return "#91e8e1";
		}
		return "#2caffe";
	}

	function EnableAllSeries() {
		if (!MainChart.value) return;
		MainChart.value.series.forEach((series) => {
			series.setVisible(true);
		});
	}

	function DisableAllSeries() {
		if (!MainChart.value) return;
		MainChart.value.series.forEach((series) => {
			series.setVisible(false);
		});
	}

	async function UpdateGraph(option: "xgrid" | "ygrid" | "build" | string) {

    // ensure we're getting the correct values
    await nextTick(() => {});

		switch (option) {
			case "xgrid":
				if (!MainChart.value) return;
				const xWidth: number = ShowXGrid.value ? 1 : 0;
				MainChart.value.update({
					xAxis: {
						gridLineWidth: xWidth * 2,
						minorGridLineWidth: xWidth,
					},
				});
				break;
			case "ygrid":
				if (!MainChart.value) return;
				const yWidth: number = ShowYGrid.value ? 1 : 0;
				MainChart.value.update({
					yAxis: {
						gridLineWidth: yWidth * 2,
						minorGridLineWidth: yWidth,
					},
				});
				break;
			default:
				BuildGraph();
		}
	}

	function SetChartType(tp: string) {
		ChartType.value = tp;
		BuildGraph();
	}

	function SetTimeLimit(months: number) {
		ChartMonthTimeLimit = months;
		BuildGraph();
	}

	async function LoadGraphData() {
		await UpdateGraph("build");
	}

	function BuildGraph() {
		const chartDiv = "LabGraphDiv";
		ChartType.value ??= "line";

		const chartTitle: string = `Lab Result By Date (${ChartType.value} chart) - ${ChartTimeString()}`;

		let newChart = Highcharts.chart(
			chartDiv,
			{
				chart: {
					type: ChartType.value ?? "line",
				},

				tooltip: {
					crosshairs: true,
					xDateFormat: "%Y-%m-%d",
					pointFormat: "<b>{point.y}</b> {series.name}",
				},

				title: {
					text: chartTitle,
					align: "center",
				},

				credits: {
					enabled: false,
				},

				yAxis: {
					title: {
						useHTML: true,
						text: "<b>Value</b>",
					},
					gridLineWidth: ShowYGrid.value ? 2 : 0,
					minorGridLineWidth: ShowYGrid.value ? 1 : 0,
				},

				xAxis: {
					type: "datetime",
					reversed: ShowReverse.value,
					gridLineWidth: ShowXGrid.value ? 2 : 0,
					minorGridLineWidth: ShowXGrid.value ? 1 : 0,
					title: {
						useHTML: true,
						text: "<b>Date</b>",
					},
				},

				exporting: {
					buttons: {
						contextButton: {
							align: "right",
							verticalAlign: "top",
						},
					},
					fallbackToExportServer: false,
				},

				navigation: {
					menuItemStyle: {
						fontSize: "0.7em",
					},
				},

				series: [
					{
						name: (GraphData?.ResultNames?.[0] ?? "unknown"), // GraphDataArr.value[7][0],
						colorByPoint: true,
					},
				],

				legend: {
					layout: "horizontal",
					align: "center",
					verticalAlign: "bottom",
				},

				plotOptions: {
					series: {
						marker: {
							symbol: "circle",
							enabled: true,
							radius: 2.5,
							lineWidth: 1,
						},
					},
				},

				responsive: {
					rules: [
						{
							condition: {
								maxWidth: 500,
							},
							chartOptions: {
								legend: {
									layout: "horizontal",
									align: "center",
									verticalAlign: "bottom",
								},
							},
						},
					],
				},
			}
			// end highchart construction
		);

		const seriesCount: number = GraphData?.SelectedLabs?.length ?? 0; // GraphDataArr.value[8];

		if (seriesCount > 1) {
			for (let sc = 1; sc < seriesCount; sc++) {
				let opt: Highcharts.SeriesOptionsType = newChart.series[0].options;
				opt.name = GraphData?.ResultNames?.[sc] ?? ""; // GraphDataArr.value[7][sc];
				newChart.addSeries(opt);
			}
		}

		for (let scolor = 0; scolor < seriesCount; scolor++) {
			newChart.series[scolor].color = GetChartColor(scolor);
		}

		// add series values
		// date limit - this will be Zulu, but should be ok
		let compareDate: Date = new Date(
			new Date().setMonth(new Date().getMonth() - ChartMonthTimeLimit)
		);

		for(let currentSeries=0; currentSeries<seriesCount; currentSeries++)
		{
			let currentItem = 0;
			GraphData.Dates?.forEach((dateValue) => {

				let skipValue: boolean = false;
				if (ChartMonthTimeLimit > 0) {
					try {
						let dt: Date = new Date(dateValue);
						if (dt < compareDate) {
							skipValue = true;
						}
					} catch {}
				}

				if (!skipValue) {
					try 
					{
						const arrValue = GraphData?.ResultValue?.[currentSeries]?.[currentItem] ?? "";
						if(arrValue)
						{
							let xValue: number = 
						  		parseFloat(arrValue);
							if(!isNaN(xValue))
							{
								//if there's a data error, look here for value dump
								//console.log(`${currentSeries}: (${dateValue},${xValue})`);
								newChart.series[0 + currentSeries].addPoint(
							{
								x: dateValue,
								y: xValue
							},
							false);
							}
						}
					}
					catch {}
				}
				currentItem++;
			});
			newChart.redraw();
		};

		MainChart.value = newChart;

    // this will update the colors
    EnableAllSeries();
	}

	
</script>
